import React from 'react'
import { Link } from 'react-router-dom'
import Header from '../../Global/Header'
import Footer from '../../Global/Footer'
import Bredcump from '../../Global/Bredcump'
import { brandUrl } from '../../../constants/brand'

const Main = () => {
  return (
    <div>
      <Header />
      <Bredcump
        Heading="Legal"
        Title="Terms of Use"
        Subtitle="Terms"
        Description="The terms and conditions for using the Noor app and related services."
      />
      <section className="row_am section_inner_padding legal_doc">
        <div className="container">
          <p className="legal_updated"><strong>Last Updated: August 23, 2026</strong></p>

          <h2>Introduction</h2>
          <p>
            Welcome to Noor. These Terms of Use (&quot;Terms&quot;) govern your use of the Noor mobile
            application (&quot;App&quot;) and related services. By downloading, installing, or using the App,
            you agree to these Terms. If you do not agree, do not use the App.
          </p>

          <h2>Acceptance of Terms</h2>
          <p>
            By accessing or using Noor, you acknowledge that you have read, understood, and agree to be
            bound by these Terms and our{' '}
            <Link to="/privacy-policy">Privacy Policy</Link>. These Terms are a legally binding agreement
            between you and us.
          </p>

          <h2>Description of Service</h2>
          <p>Noor is a health companion application that provides:</p>
          <ul>
            <li>Mental health and mindfulness lessons and modules</li>
            <li>Interactive tools (journaling, habits, fitness tracking, and related self-care tools)</li>
            <li>Personalized educational and self-care content</li>
            <li>Progress tracking within the App</li>
            <li>Subscription-based premium features</li>
            <li>AI-assisted features (where available) to help you explore your in-app data</li>
          </ul>

          <h2>Account Registration</h2>
          <h3>Account Requirements</h3>
          <ul>
            <li>You must be at least <strong>16 years old</strong> to use the App</li>
            <li>Users under 18 should use the App with a parent or guardian where appropriate</li>
            <li>You must provide accurate and complete information when creating an account</li>
            <li>You are responsible for maintaining the security of your account</li>
            <li>You are responsible for all activities under your account</li>
          </ul>
          <h3>Account Security</h3>
          <ul>
            <li>Do not share your account credentials with others</li>
            <li>Notify us promptly of any unauthorized access</li>
            <li>
              We are not liable for loss or damage from unauthorized account access you could have prevented
              with reasonable care
            </li>
          </ul>

          <h2>Subscription Terms</h2>
          <h3>Subscription Access</h3>
          <ul>
            <li>
              Noor is free to download with a <strong>7-day free trial</strong> on the monthly plan, then{' '}
              <strong>£14.99/month</strong> or <strong>£149.90/year</strong> auto-renewing subscription
              (prices may vary by store territory)
            </li>
            <li>
              Subscriptions automatically renew unless cancelled at least 24 hours before the end of the
              current period
            </li>
            <li>Cancel subscriptions through your device&apos;s App Store or Google Play account settings</li>
            <li>Subscription fees are charged to your store account</li>
            <li>Purchases are final and non-refundable except as required by law or store policy</li>
          </ul>
          <h3>Subscription Cancellation</h3>
          <ul>
            <li>You may cancel at any time</li>
            <li>Cancellation takes effect at the end of the current billing period</li>
            <li>You retain access until the end of that period</li>
            <li>No refunds for partial periods except as required by law or store policy</li>
          </ul>

          <h2>User Conduct</h2>
          <h3>Acceptable Use</h3>
          <p>You agree to use the App only for lawful purposes and in accordance with these Terms. You agree not to:</p>
          <ul>
            <li>Violate applicable laws or regulations</li>
            <li>Infringe others&apos; rights</li>
            <li>Transmit harmful, offensive, or illegal content</li>
            <li>Attempt unauthorized access to the App or its systems</li>
            <li>Interfere with or disrupt the App</li>
            <li>Use the App for unauthorized commercial purposes</li>
            <li>Abuse, harass, or misuse AI or other high-risk features</li>
          </ul>
          <h3>Prohibited Activities</h3>
          <ul>
            <li>Reverse engineering or attempting to extract source code except where law permits</li>
            <li>Creating derivative works based on the App except as allowed by law</li>
            <li>Removing or altering copyright or proprietary notices</li>
            <li>Using automated systems to access the App except as we expressly allow</li>
          </ul>

          <h2>Intellectual Property</h2>
          <h3>Our Rights</h3>
          <ul>
            <li>Content, features, and functionality of the App are owned by us or our licensors</li>
            <li>The App is protected by copyright, trademark, and other intellectual property laws</li>
            <li>
              You may not copy, modify, distribute, or create derivative works except as permitted by law or
              these Terms
            </li>
          </ul>
          <h3>Your Content</h3>
          <ul>
            <li>You retain ownership of content you create in the App</li>
            <li>
              You grant us a license to use, store, and process your content as needed to operate the App,
              provide features, and enforce these Terms
            </li>
            <li>You are responsible for ensuring you have rights to content you submit</li>
          </ul>

          <h2>AI and Automated Features</h2>
          <ul>
            <li>
              The App may include <strong>AI-assisted</strong> or automated features (for example tools that
              help you explore or summarize your in-app data)
            </li>
            <li>
              <strong>AI outputs can be wrong, incomplete, or outdated.</strong> Verify important information
              yourself
            </li>
            <li>
              AI features are for <strong>education and self-care support</strong>, not medical advice,
              diagnosis, treatment, or emergency response
            </li>
            <li>
              Do not rely on AI features for crisis decisions. In an emergency, contact local emergency
              services or a crisis line
            </li>
            <li>
              We may apply technical limits (including rate limits) to prevent abuse of AI and automated
              features
            </li>
          </ul>

          <h2>Service Integrity and Monitoring</h2>
          <ul>
            <li>
              Operating Noor safely requires <strong>service integrity and safety monitoring</strong> described
              in our <Link to="/privacy-policy">Privacy Policy</Link>
            </li>
            <li>
              This includes product events for abuse prevention, AI misuse detection, lesson quality,
              crashes, and related integrity controls
            </li>
            <li>There is <strong>no in-app opt-out</strong> for this monitoring while you use the App</li>
            <li>If you do not want this processing, do not use the App or delete your account</li>
          </ul>

          <h2>Health Disclaimer</h2>
          <h3>Not Medical Advice</h3>
          <ul>
            <li>The App provides health <strong>education</strong> and self-care tools, not medical advice</li>
            <li>Content is for informational and educational purposes only</li>
            <li>The App is not a substitute for professional medical advice, diagnosis, or treatment</li>
          </ul>
          <h3>No Medical Relationship</h3>
          <ul>
            <li>Use of the App does not create a doctor-patient or clinician-patient relationship</li>
            <li>Always seek qualified health providers for medical questions</li>
            <li>
              Never disregard professional medical advice or delay seeking it because of something in the App
            </li>
          </ul>
          <h3>Emergency and Crisis Situations</h3>
          <ul>
            <li>The App is <strong>not</strong> for medical emergencies or real-time crisis monitoring</li>
            <li>In an emergency, contact emergency services immediately</li>
            <li>
              The App may show links to third-party crisis resources. We do not operate those services and are
              not responsible for them
            </li>
            <li>
              Automated on-device journal wellbeing prompts are local product features, not clinical assessment
              or emergency services (see <Link to="/privacy-policy">Privacy Policy</Link>)
            </li>
          </ul>

          <h2>Limitation of Liability</h2>
          <h3>Disclaimer of Warranties</h3>
          <ul>
            <li>
              The App is provided <strong>&quot;as is&quot;</strong> and <strong>&quot;as available&quot;</strong>{' '}
              without warranties of any kind, to the fullest extent permitted by law
            </li>
            <li>
              We do not guarantee the App will be error-free, uninterrupted, or that AI or automated outputs
              will be accurate
            </li>
          </ul>
          <h3>Limitation of Damages</h3>
          <ul>
            <li>
              To the maximum extent permitted by law, we are not liable for indirect, incidental, special,
              consequential, or punitive damages
            </li>
            <li>
              Our total liability for claims relating to the App is limited to the amount you paid us for the
              App in the past 12 months (or £100 if greater where required by law)
            </li>
            <li>
              Some jurisdictions do not allow certain limitations; in those cases our liability is limited to
              the minimum permitted by law
            </li>
          </ul>

          <h2>Indemnification</h2>
          <p>
            You agree to indemnify and hold us harmless from claims, damages, losses, and expenses arising
            from:
          </p>
          <ul>
            <li>Your use of the App</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third party&apos;s rights</li>
            <li>Content you submit through the App</li>
          </ul>

          <h2>Termination</h2>
          <h3>By You</h3>
          <ul>
            <li>You may stop using the App at any time</li>
            <li>
              Delete your account in Profile → Account Settings → Delete Account (24-hour grace period; log in
              before then to cancel deletion)
            </li>
            <li>Cancelling a subscription does not automatically delete your account</li>
          </ul>
          <h3>By Us</h3>
          <ul>
            <li>We may suspend or terminate access if you violate these Terms or create risk for us or other users</li>
            <li>We may discontinue the App or features with reasonable notice where practicable</li>
            <li>Upon termination, your right to use the App ends immediately except as required by law</li>
          </ul>

          <h2>Changes to Terms</h2>
          <p>We may modify these Terms. We will notify you of material changes by:</p>
          <ul>
            <li>Posting updated Terms in the App</li>
            <li>Updating the &quot;Last Updated&quot; date</li>
            <li>Sending an in-app or email notice for significant changes where appropriate</li>
          </ul>
          <p>Continued use after changes become effective constitutes acceptance of the updated Terms.</p>

          <h2>Governing Law</h2>
          <p>
            These Terms are governed by the laws of <strong>England and Wales</strong>, United Kingdom,
            without regard to conflict-of-law rules.
          </p>

          <h2>Dispute Resolution</h2>
          <p>Disputes arising from these Terms or your use of the App should be resolved through:</p>
          <ul>
            <li>Good faith negotiation</li>
            <li>Mediation if negotiation fails</li>
            <li>Courts of England and Wales as a last resort</li>
          </ul>
          <p>Nothing in these Terms limits mandatory consumer rights under applicable law.</p>

          <h2>Severability</h2>
          <p>If any provision is unenforceable, the remaining provisions remain in full effect.</p>

          <h2>Entire Agreement</h2>
          <p>
            These Terms, together with our <Link to="/privacy-policy">Privacy Policy</Link>, constitute the
            entire agreement between you and us regarding the App.
          </p>

          <h2>Contact Information</h2>
          <p>If you have questions about these Terms, contact us:</p>
          <p>
            <strong>Email</strong>: support@noorhc.app<br />
            <strong>Website</strong>: {brandUrl('marketing')}
          </p>

          <p className="legal_footer">
            <em>
              By using Noor, you acknowledge that you have read, understood, and agree to these Terms of
              Use.
            </em>
          </p>
        </div>
      </section>
      <Footer />
    </div>
  )
}

export default Main
